import React, { useState } from 'react';
import { Container } from "react-bootstrap";
import { useData } from '../../context/DataContext';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip } from '@mui/material';
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import EditArtistModel from './EditArtistModel';
import AddArtistModel from './AddArtistModel';
import Loading from '../Loading/Loading';
import MaterialReactTableWithSearch from '../MaterialTable/MaterialReactTableWithSearch';
import { fetchFromApi } from '../../config/api';
import API_ENDPOINTS from '../../config/apiConfig';

function Artists() {
    const { artistData, loading, error, refetch, fetchArtistData } = useData();
    const [editModal, setEditModal] = useState(false);
    const [addModal, setAddModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    if (loading) return <Loading />;
    if (error) return (
        <div style={{ padding: 24 }}>
            <p style={{ color: 'red' }}>Error: {error}</p>
            <button className="btn-primary-orange" onClick={refetch}>Retry</button>
        </div>
    );
    if (!artistData || artistData.length === 0) return <div style={{ padding: 24 }}>No artist data available.</div>;

    const activeEditModal = (row) => { setEditData(row.original); setEditModal(true); };

    const deleteRow = async (row) => {
        if (window.confirm("Are you sure you want to delete this artist?")) {
            setDeleteLoading(true);
            try {
                const data = await fetchFromApi(API_ENDPOINTS.DELETE_ARTIST, 'POST', { id: row.original.id });
                if (data.status === "success") await fetchArtistData();
            } catch (err) {
                setDeleteError("Failed to delete artist. Please try again.");
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    const renderRowActions = ({ row }) => (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Edit">
                <IconButton size="small" onClick={() => activeEditModal(row)}>
                    <ModeEditOutlineSharpIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton size="small" onClick={() => deleteRow(row)} disabled={deleteLoading}>
                    <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                </IconButton>
            </Tooltip>
        </Box>
    );

    const columns = [
        { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id', size: 60 },
        {
            accessorKey: 'image', header: 'Image',
            accessorFn: (row) => row.image || '-',
            Cell: ({ cell }) => {
                const url = cell.getValue();
                return url === '-' ? <span>-</span> : (
                    <img src={url} alt="Artist" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />
                );
            },
        },
        { accessorKey: 'name', header: 'Name', accessorFn: (row) => row.name || "-" },
        { accessorKey: 'email', header: 'Email', accessorFn: (row) => row.email || "-" },
        {
            accessorKey: 'mobile', header: 'Mobile',
            accessorFn: (row) => {
                const n = row.mobile;
                return n ? n.replace(/[^\d]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : "-";
            },
        },
        { accessorKey: 'address', header: 'Address', accessorFn: (row) => row.address || "-" },
        {
            accessorKey: 'about', header: 'About',
            accessorFn: (row) => row.about ? row.about.slice(0, 80) + (row.about.length > 80 ? "…" : "") : "-",
        },
        { accessorKey: 'inserted_time', header: 'Added Date', accessorFn: (row) => row.inserted_time || "-" },
        {
            id: 'actions', header: 'Actions', Cell: renderRowActions, size: 100,
            muiTableBodyCellProps: { sx: { position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 1 } },
            muiTableHeadCellProps: { sx: { position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 1 } },
        }
    ];

    return (
        <Container fluid className="px-0">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Artists</h1>
                    <p className="page-subtitle">{artistData.length} artists in total</p>
                </div>
                <button className="btn-primary-orange" onClick={() => setAddModal(true)}>
                    <i className="fa-solid fa-plus"></i> Add Artist
                </button>
            </div>

            <div className="table-container">
                <MaterialReactTableWithSearch columns={columns} data={artistData} />
            </div>

            {deleteError && <p style={{ color: '#ef4444', marginTop: 12, fontSize: '0.875rem' }}>{deleteError}</p>}

            {editModal && (
                <EditArtistModel
                    show={editModal}
                    editData={editData}
                    onHide={() => { setEditModal(false); setEditData({}); }}
                />
            )}
            {addModal && <AddArtistModel show={addModal} onHide={() => setAddModal(false)} />}
        </Container>
    );
}

export default Artists;
